import { useMemo } from 'react';
import { RouterLink } from '@redux-first-router-link';
import { useTracking } from 'react-tracking';

import { Grid, Box, Text, useTheme } from '@simplywallst/ui-core';
import RewardSolid from '@simplywallst/ui-core/icons/RewardSolid';
import WarningSolid from '@simplywallst/ui-core/icons/WarningSolid';

import PositiveNegativeValue from '@/components/PositiveNegativeValue';
import {
  Snowflake,
  getSnowflakeFillColor,
} from '@simplywallst/ui-charts/Snowflake';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';
import { useHoldingsLimits } from '@/hooks/portfolio/useHoldingsLimits';
import { ROUTE_PORTFOLIO } from '@/constants/routes';

import { formatCurrencyToScaleTruncated } from '@/utilities/portfolio/formatValue';
import { formatterPercentage } from '@/utilities/format';
import { pluralise } from '@/utilities/pluralise';
import { isIncompleteManualPortfolio } from '../../../../components/PortfolioManual/utils';

import { MiniPortfolioPerformanceChart } from '../MiniPortfolioPerformanceChart';
import { SnowflakeBlur } from './styled';

const getCount = (count: number | null) => {
  if (count === null) return '0';
  if (count > 99) return '+99';
  return String(count);
};

type Props = {
  portfolio: Portfolio;
};

export const PopulatedState = ({ portfolio }: Props) => {
  const theme = useTheme();

  const { trackEvent } = useTracking({
    type: 'track',
    subject: 'newTile',
  });

  const { holdings, excludedHoldings } = useHoldingsLimits({
    portfolioHoldings: portfolio.holdings,
  });
  const {
    value,
    roi,
    estimatedDividendYield,
    return1y,
    risksCount,
    rewardsCount,
  } = portfolio.analysis;

  const hasRisks = risksCount !== null && risksCount > 0;
  const hasRewards = rewardsCount !== null && rewardsCount > 0;

  const holdingsCountText = `${holdings.length} ${pluralise(
    holdings.length,
    'holding',
    'holdings'
  )}`;
  const { incompleteHoldingCount } = isIncompleteManualPortfolio(holdings);
  const incompleteHoldingCountText =
    incompleteHoldingCount > 0 ? `${incompleteHoldingCount} require data` : '';
  const excludedHoldingsCountText =
    excludedHoldings.length > 0 ? `${excludedHoldings.length} excluded` : '';

  const normalisedPoints: [number, number, number, number, number] =
    useMemo(() => {
      const { scores } = portfolio.snowflake;
      const { value, future, past, health, dividend } = scores;

      return [
        (value || 0) + 1,
        (future || 0) + 1,
        (past || 0) + 1,
        (health || 0) + 1,
        (dividend || 0) + 1,
      ];
    }, [portfolio.snowflake]);

  return (
    <RouterLink
      to={{
        type: ROUTE_PORTFOLIO,
        payload: { portfolioId: portfolio.id },
      }}
      noUnderline
      data-cy-id={`portfolio-card-${portfolio.id}`}
      onClick={() => {
        trackEvent({
          action: 'click',
        });
      }}
    >
      <Box
        minWidth="288px"
        backgroundColor="noir"
        borderRadius="x1"
        overflow="hidden"
      >
        <Grid templateRows="auto 55px 56px" gap="8px">
          <Box paddingXSm="x3" paddingX="x2" paddingY="x2">
            <Grid templateColumns="auto 80px">
              <Grid templateRows="45px auto" gap="8px">
                <Grid templateRows="auto auto">
                  <Text typography="baseStrong" isTruncated>
                    {portfolio.name}
                  </Text>
                  <Text typography="tiny" color="textSofter">
                    {[
                      holdingsCountText,
                      incompleteHoldingCountText,
                      excludedHoldingsCountText,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </Text>
                </Grid>
                <Grid
                  templateColumns="repeat(auto-fit, minmax(max-content, 70px))"
                  templateColumnsSm="repeat(3, max-content)"
                  gap="16px"
                >
                  <Grid>
                    <Text typography="xSmall">
                      {formatCurrencyToScaleTruncated(
                        value,
                        portfolio.currencyISO || 'USD'
                      )}
                    </Text>
                    <Text typography="tiny" color="textSofter">
                      Total Value
                    </Text>
                  </Grid>
                  <Grid>
                    <Text typography="xSmall">
                      <PositiveNegativeValue
                        value={roi}
                        data-testid="total-return"
                      >
                        {formatterPercentage(roi)}
                      </PositiveNegativeValue>
                    </Text>
                    <Text typography="tiny" color="textSofter">
                      Total Return
                    </Text>
                  </Grid>
                  <Grid>
                    <Text typography="xSmall">
                      {formatterPercentage(estimatedDividendYield)}
                    </Text>
                    <Text typography="tiny" color="textSofter">
                      Div. Yield
                    </Text>
                  </Grid>
                </Grid>
              </Grid>
              <Grid justifyContent="center" alignItems="start">
                <Box width="80px" height="80px" position="relative">
                  <SnowflakeBlur
                    data-testid="snowflake-blur"
                    backgroundColor={getSnowflakeFillColor(normalisedPoints)}
                  />
                  <Snowflake
                    data-testid="snowflake"
                    points={normalisedPoints}
                    width={80}
                  >
                    <Snowflake.DotMarkers />
                  </Snowflake>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Grid alignItems="center">
            <MiniPortfolioPerformanceChart portfolioId={portfolio.id} />
          </Grid>
          <Box paddingXSm="x3" paddingX="x2" paddingTop="x1" paddingBottom="x2">
            <Grid
              templateColumnsSm="auto 75px"
              templateColumns="auto max-content"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid templateColumns="auto auto" gapSm="8px" gap="4px">
                <Box borderRadius="x0_5" backgroundColor="spaceGray">
                  <Grid
                    padding="x0_5"
                    paddingRight="x1_5"
                    paddingLeft="x1"
                    borderRadius="x0_5"
                    templateColumns="max-content max-content"
                    alignItems="center"
                    gap="2px"
                    opacity={hasRisks ? 1 : 0.3}
                    data-testid="risk-count-container"
                  >
                    <Box position="relative" width={24} height={24}>
                      <Box
                        position="absolute"
                        backgroundColor="white"
                        borderRadius="x3"
                        width={12}
                        height={12}
                        transform="translate(50%, 50%)"
                      />
                      <Box position="relative">
                        <WarningSolid fill={theme.color.bad} />
                      </Box>
                    </Box>
                    <Text typography="tiny">
                      {`${getCount(risksCount)} ${pluralise(
                        risksCount,
                        'Risk',
                        'Risks'
                      )}`}
                    </Text>
                  </Grid>
                </Box>
                <Box borderRadius="x0_5" backgroundColor="spaceGray">
                  <Grid
                    padding="x0_5"
                    paddingRight="x1_5"
                    paddingLeft="x1"
                    borderRadius="x0_5"
                    templateColumns="max-content max-content"
                    alignItems="center"
                    opacity={hasRewards ? 1 : 0.3}
                    data-testid="reward-count-container"
                  >
                    <RewardSolid fill={theme.color.good} />
                    <Text typography="tiny">
                      {`${getCount(rewardsCount)} ${pluralise(
                        rewardsCount,
                        'Reward',
                        'Rewards'
                      )}`}
                    </Text>
                  </Grid>
                </Box>
              </Grid>
              <Grid>
                <Box
                  borderRadius="x0_5"
                  style={{
                    backgroundColor:
                      return1y === null
                        ? theme.color.whiteRGBA(0.3)
                        : return1y > 0
                          ? theme.color.goodRGBA(0.1)
                          : theme.color.badRGBA(0.1),
                  }}
                >
                  <Grid
                    padding="x0_5"
                    paddingX="x1"
                    borderRadius="x0_5"
                    justifyContent="center"
                    alignItems="center"
                    templateColumns="max-content max-content"
                    templateRows="24px"
                  >
                    <Text typography="tiny" color="textSofter">
                      1Y{' '}
                      <PositiveNegativeValue
                        value={return1y}
                        data-testid="annual-return"
                      >
                        {formatterPercentage(return1y)}
                      </PositiveNegativeValue>
                    </Text>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Box>
    </RouterLink>
  );
};
